import React from 'react';
import { useLocation, useParams } from 'react-router-dom';
import {
	UI_CASH_MANAGEMENT_FEATURE_ENABLED,
	useAuthContext,
} from '../../../context/auth';
import { useRouteMatch } from '../../../util/useRouteMatch';
import TreeNode, {
	TreeData,
	TreeItemData,
	TreeItemInterface,
} from '../../layouts/SidePanel/TreeNode';

// TODO: migrate more policyholder routes here
export interface PolicyholderRoutes {
	INVOICE: string;
	INVOICES: string;
	PAYMENTS: string;
	NEW_PAYMENT: string;
	PAYMENT: string;
	EDIT_PAYMENT: string;
	DISBURSEMENTS: string;
	DISBURSEMENT: string;
	EDIT_DISBURSEMENT: string;
	CATCHUPS: string;
	CATCHUP: string;
}

// TODO: migrate more policyholder routes here
export const POLICYHOLDER_ROUTES: PolicyholderRoutes = {
	INVOICE: '/policyholder/:policyholderId/invoices/:invoiceId',
	INVOICES: '/policyholder/:policyholderId/invoices',
	PAYMENTS: '/policyholder/:policyholderId/payments',
	NEW_PAYMENT: '/policyholder/:policyholderId/payments/new',
	PAYMENT: '/policyholder/:policyholderId/payments/:paymentLocator',
	EDIT_PAYMENT: '/policyholder/:policyholderId/payments/:paymentLocator/edit',
	DISBURSEMENTS: '/policyholder/:policyholderId/disbursements',
	DISBURSEMENT:
		'/policyholder/:policyholderId/disbursements/:disbursementLocator',
	EDIT_DISBURSEMENT:
		'/policyholder/:policyholderId/disbursements/:disbursementLocator/edit',
	CATCHUPS: '/policyholder/:policyholderId/catchUps',
	CATCHUP: '/policyholder/:policyholderId/catchUps/:catchUpLocator',
};

const PolicyholderSidebar = () => {
	const authData = useAuthContext();
	const cashManagementEnabled =
		authData?.auth?.configProperties?.[UI_CASH_MANAGEMENT_FEATURE_ENABLED];
	const { hasPermission } = authData;
	const params = useParams();
	const currentPath = useLocation();
	const { invoiceId, policyholderId, paymentLocator } = params;
	const paymentsExpanded = paymentLocator ? (['Payments'] as string[]) : null;
	const invoicesExpanded = invoiceId ? (['Invoices'] as string[]) : null;
	const itemsExpanded = paymentsExpanded || invoicesExpanded || [];
	const locator = policyholderId;
	const policyholderLink = `/policyholder/${locator}`;
	const overviewLink = `${policyholderLink}/overview`;
	const policiesLink = `${policyholderLink}/policies`;
	const invoicesLink = `${policyholderLink}/invoices`;
	const paymentsLink = `${policyholderLink}/payments`;
	const disbursementsLink = `${policyholderLink}/disbursements`;
	const catchUpsLink = `${policyholderLink}/catchUps`;

	const routeName = currentPath.pathname.split('/').pop();
	const routeMatch = useRouteMatch(
		Object.keys(POLICYHOLDER_ROUTES).map(
			(key) => POLICYHOLDER_ROUTES[key as keyof PolicyholderRoutes],
		),
	);
	const policyholderRouteName = routeMatch?.pattern?.path || '';

	const getRootItem = () => {
		const children = [];

		if (hasPermission('policyholder.fetch')) {
			children.push('Overview');
		}

		if (
			(hasPermission('policyholder.fetch') && hasPermission('policy.fetch')) ||
			hasPermission('*.fetch')
		) {
			children.push('Policies');
		}

		if (
			(hasPermission('policyholder.fetch') && hasPermission('invoice.fetch')) ||
			hasPermission('*.fetch')
		) {
			children.push('Invoices');
		}

		if (cashManagementEnabled && hasPermission('payment.fetch')) {
			children.push('Payments');
		}

		if (cashManagementEnabled && hasPermission('disbursement.fetch')) {
			children.push('Disbursements');
		}
		if (cashManagementEnabled && hasPermission('catchUpInvoice.fetch')) {
			children.push('Catch Ups');
		}
		return {
			id: 'root',
			children,
			hasChildren: true,
			isExpanded: true,
			isChildrenLoading: false,
			data: {
				title: 'root',
			},
		};
	};

	const getItems = () => {
		let items: TreeItemData = {
			root: getRootItem(),
			Overview: {
				id: 'Overview',
				children: [],
				hasChildren: false,
				isExpanded: true,
				isChildrenLoading: false,
				data: {
					title: 'Overview',
					link: overviewLink,
					selected: routeName === 'overview',
				},
			},
			Policies: {
				id: 'Policies',
				children: [],
				hasChildren: false,
				isExpanded: true,
				isChildrenLoading: false,
				data: {
					title: 'Policies',
					link: policiesLink,
					selected: routeName === 'policies',
				},
			},
		};

		const invoiceGroupItem: TreeItemInterface = {
			id: 'Invoices',
			children: [],
			hasChildren: false,
			isExpanded: itemsExpanded.includes('Invoices'),
			isChildrenLoading: false,
			data: {
				title: 'Invoices',
				link: invoicesLink,
				selected:
					policyholderRouteName === POLICYHOLDER_ROUTES.INVOICES ||
					policyholderRouteName === POLICYHOLDER_ROUTES.INVOICE,
			},
		};

		items = {
			...items,
			Invoices: invoiceGroupItem,
		};

		if (cashManagementEnabled) {
			items['Payments'] = {
				id: 'Payments',
				children: [],
				hasChildren: false,
				isExpanded: false,
				isChildrenLoading: false,
				data: {
					title: 'Payments',
					link: paymentsLink,
					selected:
						policyholderRouteName === POLICYHOLDER_ROUTES.PAYMENTS ||
						policyholderRouteName === POLICYHOLDER_ROUTES.NEW_PAYMENT ||
						policyholderRouteName === POLICYHOLDER_ROUTES.EDIT_PAYMENT ||
						policyholderRouteName === POLICYHOLDER_ROUTES.PAYMENT,
				},
			};

			items['Disbursements'] = {
				id: 'Disbursements',
				children: [],
				hasChildren: false,
				isExpanded: false,
				isChildrenLoading: false,
				data: {
					title: 'Disbursements',
					link: disbursementsLink,
					selected:
						policyholderRouteName === POLICYHOLDER_ROUTES.DISBURSEMENTS ||
						policyholderRouteName === POLICYHOLDER_ROUTES.DISBURSEMENT ||
						policyholderRouteName === POLICYHOLDER_ROUTES.EDIT_DISBURSEMENT,
				},
			};
			items['Catch Ups'] = {
				id: 'Catch Ups',
				children: [],
				hasChildren: false,
				isExpanded: false,
				isChildrenLoading: false,
				data: {
					title: 'Catch-Ups',
					link: catchUpsLink,
					selected:
						policyholderRouteName === POLICYHOLDER_ROUTES.CATCHUPS ||
						policyholderRouteName === POLICYHOLDER_ROUTES.CATCHUP,
				},
			};
		}

		return items;
	};

	const initializeTree = () => {
		const sidePanelData: TreeData = {
			rootId: 'root',
			items: getItems(),
		};

		return sidePanelData;
	};
	const tree = initializeTree();

	return <TreeNode treeData={tree} defaultExpanded={itemsExpanded} />;
};

export default PolicyholderSidebar;
